import { useEffect, useState } from "react";
import { Alert, Box, ColumnLayout, SpaceBetween, Spinner } from "@cloudscape-design/components";
import { apiFetch } from "../apiFetch";
import type { DetailWidget, RecordFilter, Widget } from "./viewContract";
import { buildFetchQuery, readFieldString } from "./widgetData";
import { useWidgetRenderContext } from "./widgetRegistry";

/**
 * Renders a single SELF-CONTAINED `detail` widget (render-pipeline stage 4).
 *
 * A detail widget pins to ONE record and shows it as a key-value grid (like
 * Ec2InstanceDetail's ColumnLayout block). Given its own widget node + the shared schema
 * context, this component:
 *  - resolves its OWN collection uid -> REST pluralName and short name,
 *  - builds its OWN Strapi 5 query with the SAME field model as the table (shared
 *    `buildFetchQuery`: indexed `fields[]` for main scalars, nested
 *    `populate[rel][fields][]` for one-hop relation paths),
 *  - appends an exact-match `filters[<field>][$eq]=<value>` per record filter, where the
 *    value is a literal or a URL view-param,
 *  - fetches with `pagination[pageSize]=2` to cheaply detect >1,
 *  - manages its OWN loading / fetch-error / result state with SCOPED Alerts.
 *
 * SINGLE-RECORD SEMANTICS (INFO, never error):
 *  - a `param`-sourced record filter whose URL value is ABSENT/EMPTY => the record is
 *    UNRESOLVED; we SHORT-CIRCUIT to an info message without fetching an arbitrary record,
 *  - exactly 1 record => the key-value grid,
 *  - 0 records OR >1 records => a scoped INFO Alert explaining the mismatch.
 * A genuine network/HTTP failure is still surfaced as an ERROR Alert.
 *
 * REFETCH: the data effect is keyed on the resolved record-filter values (literal values
 * and the CURRENT URL values of any bound params), so navigating to a different ?param=
 * value refetches; unrelated query-param changes do not.
 */

/**
 * Resolve each record filter to its exact-match value. Returns either the concrete
 * `filters` query fragment, or `{ unresolved: true }` when a `param`-sourced filter has
 * no (non-empty) URL value — meaning the target record cannot be identified.
 */
function resolveRecordFilters(
  recordFilters: RecordFilter[],
  urlParams: Record<string, string>
): { unresolved: true } | { unresolved: false; query: string } {
  const parts: string[] = [];
  for (const rf of recordFilters) {
    const raw = rf.source === "literal" ? rf.value ?? "" : urlParams[rf.param ?? ""] ?? "";
    const value = raw.trim();
    if (rf.source === "param" && value === "") {
      // A param-sourced filter with no URL value => record cannot be resolved.
      return { unresolved: true };
    }
    parts.push(`filters[${encodeURIComponent(rf.field)}][$eq]=${encodeURIComponent(value)}`);
  }
  return { unresolved: false, query: parts.join("&") };
}

export default function DetailWidgetView({ widget }: { widget: Widget }) {
  // Narrow to the detail shape. The registry only routes `type === "detail"` here.
  const detail = widget as DetailWidget;
  const { schema, pluralNameFor, urlParams } = useWidgetRenderContext();

  // --- Resolve this detail's OWN collection uid -> plural + short name ----------------
  const resolvedPlural = pluralNameFor(detail.collection);
  const mainCt = schema.find((c) => c.uid === detail.collection);
  const collectionShortName = mainCt?.apiName ?? detail.collection.split(".").pop() ?? "";

  // --- Scoped data lifecycle state ----------------------------------------------------
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState<string | null>(null);
  // null = unresolved (missing param); otherwise the array of matched records (0/1/N).
  const [records, setRecords] = useState<Record<string, any>[] | null>(null);

  const recordFilters = detail.recordFilters ?? [];
  const relationsKey = detail.relations.join("|");
  const selectedKey = detail.selected.join("|");
  // Stable dep key over the RESOLVED record-filter values (literal values + CURRENT URL
  // values of bound params). Navigating to a different bound ?param= value refetches.
  const recordFilterKey = JSON.stringify(
    recordFilters.map((rf) => [
      rf.field,
      rf.source,
      rf.source === "literal" ? rf.value ?? "" : urlParams[rf.param ?? ""] ?? "",
    ])
  );

  useEffect(() => {
    if (!resolvedPlural) {
      // Nothing to fetch — the render below shows the scoped "content-type missing" Alert.
      setLoading(false);
      return;
    }
    // Short-circuit: a param-sourced filter with no URL value => unresolved -> info.
    const resolved = resolveRecordFilters(recordFilters, urlParams);
    if (resolved.unresolved) {
      setRecords(null);
      setFetchError(null);
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      setFetchError(null);
      try {
        const fieldsQuery = buildFetchQuery(detail.selected, detail.relations, collectionShortName);
        // pageSize=2 is enough to distinguish 0 / exactly-1 / >1 cheaply.
        const query = [fieldsQuery, resolved.query, "pagination[pageSize]=2"]
          .filter(Boolean)
          .join("&");

        const dataRes = await apiFetch(`/${resolvedPlural}?${query}`);
        if (!dataRes.ok) {
          if (!cancelled) {
            setFetchError(`Failed to load data from "${resolvedPlural}" (${dataRes.status}).`);
            setLoading(false);
          }
          return;
        }
        const dataJson = await dataRes.json();
        const recs: Record<string, any>[] = Array.isArray(dataJson?.data) ? dataJson.data : [];
        if (!cancelled) setRecords(recs);
      } catch (e) {
        if (!cancelled) setFetchError((e as Error).message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resolvedPlural, collectionShortName, relationsKey, selectedKey, recordFilterKey]);

  // --- Render-time validation: unknown collection ------------------------------------
  if (!resolvedPlural) {
    return (
      <Alert type="error" header="Detail widget error">
        The content-type "{detail.collection || "(empty)"}" does not exist.
      </Alert>
    );
  }

  if (loading) {
    return (
      <Box textAlign="center" padding="l">
        <Spinner size="large" />
      </Box>
    );
  }

  if (fetchError) {
    return (
      <Alert type="error" header="Failed to load detail data">
        {fetchError}
      </Alert>
    );
  }

  // Unresolved (missing param) OR zero matches => INFO, never error.
  if (records === null || records.length === 0) {
    return (
      <Alert type="info" header="No matching record">
        This detail view has no matching record for the current filters/parameters.
      </Alert>
    );
  }

  // More than one match => INFO stating the count.
  if (records.length > 1) {
    return (
      <Alert type="info" header="Multiple records matched">
        This detail view expects a single record but matched {records.length}. Adjust the
        filter or parameter.
      </Alert>
    );
  }

  // Exactly one record => the key-value grid.
  const record = records[0];
  return (
    <SpaceBetween size="m">
      <ColumnLayout columns={detail.columns} variant="text-grid">
        {detail.items.map((item, index) => (
          <div key={`item-${index}`}>
            <Box variant="awsui-key-label">{item.label}</Box>
            <div>{readFieldString(record, item.field, collectionShortName)}</div>
          </div>
        ))}
      </ColumnLayout>
    </SpaceBetween>
  );
}
